<script setup lang="ts">
import type { ApiBlockGridArea } from "@edco/apollo";
import { kebabCase } from "scule";

export type ColumnAlignment = "top" | "center" | "bottom" | "space evenly" | "space around" | "space between" | "stretch";

const props = defineProps<{
  areas?: ApiBlockGridArea[]
  layoutClass: string,
  theme?: string | null,
  leftColumnAlignment?: ColumnAlignment,
  rightColumnAlignment?: ColumnAlignment,
  noTopPadding?: boolean | null,
  noBottomPadding?: boolean | null,
}>();

// Root classes for the component
const classes = computed<String[]>( () => {
  const result: string[] = [props.layoutClass, "column-block"];

  if ( props.theme ) {
    result.push( `theme--${props.theme.toLowerCase()}` );
  }

  if ( props.leftColumnAlignment ) {
    result.push( `column-block--left-${kebabCase( props.leftColumnAlignment )}`)
  } else if ( props.areas?.length === 2 ) {
    const leftCount = props.areas[0].items.length;
    const rightCount = props.areas[1].items.length;

    if (leftCount <= 2 && rightCount <= 2) {
      // If neither column has much in it, align to centre
      result.push('column-block--left-centre');
    } else if (leftCount > 2 && rightCount > 2) {
      // If both columns have a lot, align to top
      result.push('column-block--left-top');
    } else if (leftCount <= 2 && rightCount > 2) {
      // If the left column doesn't have much but the right does, centre align
      result.push('column-block--left-centre');
    } else if (leftCount > 2 && rightCount <= 2) {
      // If the right column doesn't have much but the left does, top align
      result.push('column-block--left-top');
    }
  }

  if ( props.rightColumnAlignment ) {
    result.push( `column-block--right-${kebabCase( props.rightColumnAlignment )}`)
  } else if ( props.areas?.length === 2 ) {
    const leftCount = props.areas[0].items.length;
    const rightCount = props.areas[1].items.length;

    if (leftCount <= 2 && rightCount <= 2) {
      // If neither column has much in it, align to centre
      result.push('column-block--right-centre');
    } else if (leftCount > 2 && rightCount > 2) {
      // If both columns have a lot, align to top
      result.push('column-block--right-top');
    } else if (leftCount <= 2 && rightCount > 2) {
      // If the left column doesn't have much but the right does, top align
      result.push('column-block--right-top');
    } else if (leftCount > 2 && rightCount <= 2) {
      // If the right column doesn't have much but the left does, centre align
      result.push('column-block--right-centre');
    }
  }

  if ( props.noTopPadding ) {
    result.push('column-block--no-top-padding');
  }

  if ( props.noBottomPadding ) {
    result.push('column-block--no-bottom-padding');
  }

  return result;
} );

</script>

<template>
  <div :class="classes">
    <div class="container">
      <block-grid-areas class="column-block__areas" :areas="areas" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.column-block {
  // padding: var(--s-8) 0;
  padding: 20px 0;
  background-color: var(--theme-background-color);
  color: var(--theme-text-color);

  @media( max-width: 767px ) {
    padding: var(--s-4) 0;
  }

  &--no-top-padding {
    padding-top: 0;
  }

  &--no-bottom-padding {
    padding-bottom: 0;
  }

  @media ( min-width: 992px ) {
    &.two-column ::v-deep( .column-block__areas > .umb-block-grid__area:first-child ) {
      margin-right: 100px;
    }

    &.two-column ::v-deep( .column-block__areas > .umb-block-grid__area:last-child ) {
      margin-left: 100px;
    }


    @mixin justify-areas($block, $child, $justify) {
      &.two-column.#{$block} ::v-deep( .column-block__areas > .umb-block-grid__area:#{$child} ) {
        justify-content: $justify;
      }
    }

    @each $block in left, right {
      @each $position, $justify in (
        top: flex-start,
        centre: center,
        bottom: flex-end,
        space-evenly: space-evenly,
        space-between: space-between,
        space-around: space-around
      ) {
        @include justify-areas(column-block--#{$block}-#{$position}, if($block == left, first-child, last-child), $justify);
      }
    }
  }
}
</style>
